/***
 *
 *   IntegrationTriggerConfirm
 *   Landing page the Karbon user reaches after clicking "Send Verification"
 *   in a Karbon IntegrationTask.
 *
 *   - For a Contact-type task: shows a 5-second countdown that auto-sends
 *     by default; a Cancel button lets the user abort before the POST fires.
 *   - For a ClientGroup-type task: renders a radio list of group members and
 *     lets the user pick one to verify. Members already in flight, members
 *     without an email, and organization members are shown disabled with the
 *     reason. One IntegrationTask still produces one verification.
 *
 **********/

import React, { Fragment, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Axios from "axios";
import { Row, Card, Button, Loader, useAPI } from "components/lib";
import logo from "../../assets/IdentityCheck_Logo_RGB_Purple.png";

const COUNTDOWN_SECONDS = 5;

const INELIGIBLE_BADGE = {
  in_flight: "In progress",
  no_email: "No email",
  organization: "Organization",
};

export function IntegrationTriggerConfirm() {
  const { token } = useParams();
  const lookup = useAPI(`/api/public/integration/trigger/${token}`);

  const state = lookup?.data?.state;
  const clientType = lookup?.data?.clientType;

  if (lookup.loading) {
    return <Loader />;
  }

  return (
    <Row color="white">
      <Card>
        <div className="flex items-center justify-center">
          <img src={logo} width="350px" alt="IdentityCheck" />
        </div>
        <br />
        <br />

        {state === "notfound" && <NotFoundView />}

        {state === "used" && <UsedView lookup={lookup} clientType={clientType} />}

        {state === "ready" && clientType === "ClientGroup" && (
          <GroupPicker token={token} lookup={lookup} />
        )}

        {state === "ready" && clientType !== "ClientGroup" && (
          <ContactCountdown token={token} lookup={lookup} />
        )}
      </Card>
    </Row>
  );
}

// --- Shared sub-views --------------------------------------------------------

function NotFoundView() {
  return (
    <Fragment>
      <h1 style={{ textAlign: "center", fontSize: "28px" }}>
        Invalid or expired link
      </h1>
      <p style={{ textAlign: "center" }}>
        This verification link can't be found. It may have expired or been
        removed. Please create a new IntegrationTask in Karbon to try again.
      </p>
    </Fragment>
  );
}

function UsedView({ lookup, clientType }) {
  const verificationUrl = lookup.data?.verificationUrl;

  const heading =
    clientType === "ClientGroup"
      ? "Verification already sent"
      : "Verification already sent";

  const memberLabel =
    clientType === "ClientGroup"
      ? [lookup.data?.selectedFirstName, lookup.data?.selectedLastName]
          .filter(Boolean)
          .join(" ")
          .trim()
      : null;

  return (
    <Fragment>
      <h1 style={{ textAlign: "center", fontSize: "28px" }}>{heading}</h1>
      <p style={{ textAlign: "center" }}>
        {memberLabel
          ? `A verification was already triggered for ${memberLabel}. The session is still active and the end client can complete it using the link below.`
          : "This verification was already triggered. The session is still active and the end client can complete it using the link below."}
      </p>
      {verificationUrl && (
        <div
          className="flex justify-center items-center text-center"
          style={{ marginTop: "20px" }}
        >
          <Button
            text="View verification session"
            action={() => window.open(verificationUrl, "_blank")}
          />
        </div>
      )}
    </Fragment>
  );
}

// --- Contact (single-contact) flow ------------------------------------------

function ContactCountdown({ token, lookup }) {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [cancelled, setCancelled] = useState(false);
  const [submitState, setSubmitState] = useState("idle");
  const [submitResult, setSubmitResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (cancelled) return undefined;
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => s - 1);
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [cancelled]);

  useEffect(() => {
    if (cancelled || secondsLeft > 0) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    setSubmitState("submitting");
    Axios.post(`/api/public/integration/trigger/${token}`)
      .then((res) => {
        setSubmitResult(res.data?.data);
        setSubmitState("done");
      })
      .catch((err) => {
        const existingUrl = err.response?.data?.verificationUrl;
        if (existingUrl) {
          setSubmitResult({ verificationUrl: existingUrl, alreadySent: true });
          setSubmitState("done");
        } else {
          setSubmitError(err.response?.data?.error || "Failed to send verification");
          setSubmitState("error");
        }
      });
  }, [secondsLeft, cancelled, token]);

  const handleCancel = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setCancelled(true);
  };

  return (
    <Fragment>
      {submitState === "idle" && !cancelled && (
        <Fragment>
          <h1 style={{ textAlign: "center", fontSize: "28px" }}>
            Sending verification to {lookup.data?.email}
          </h1>
          <p style={{ textAlign: "center", fontSize: "20px" }}>
            Starting in <strong>{secondsLeft}</strong>{" "}
            {secondsLeft === 1 ? "second" : "seconds"}...
          </p>
          <div
            className="flex justify-center items-center text-center"
            style={{ marginTop: "20px" }}
          >
            <Button text="Cancel" action={handleCancel} color="red" />
          </div>
        </Fragment>
      )}

      {cancelled && submitState === "idle" && (
        <Fragment>
          <h1 style={{ textAlign: "center", fontSize: "28px" }}>Cancelled</h1>
          <p style={{ textAlign: "center" }}>
            No verification email was sent. You can close this window and click
            the Karbon link again to retry.
          </p>
        </Fragment>
      )}

      {submitState === "submitting" && (
        <Fragment>
          <h1 style={{ textAlign: "center", fontSize: "28px" }}>
            Sending verification...
          </h1>
          <Loader />
        </Fragment>
      )}

      {submitState === "done" && (
        <SentView
          alreadySent={submitResult?.alreadySent}
          email={lookup.data?.email}
          verificationUrl={submitResult?.verificationUrl}
        />
      )}

      {submitState === "error" && (
        <Fragment>
          <h1 style={{ textAlign: "center", fontSize: "28px" }}>
            Something went wrong
          </h1>
          <p style={{ textAlign: "center" }}>{submitError}</p>
        </Fragment>
      )}
    </Fragment>
  );
}

// --- ClientGroup flow --------------------------------------------------------

function GroupPicker({ token, lookup }) {
  const groupName = lookup.data?.groupName;
  const members = lookup.data?.members || [];
  const eligibleMembers = members.filter((m) => m.eligible);

  const [selected, setSelected] = useState(
    eligibleMembers[0]?.contact_key || null
  );
  const [submitState, setSubmitState] = useState("idle");
  const [submitResult, setSubmitResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const selectedMember = members.find((m) => m.contact_key === selected) || null;

  const handleSend = () => {
    if (!selected) return;
    setSubmitState("submitting");
    Axios.post(`/api/public/integration/trigger/${token}`, {
      contactKey: selected,
    })
      .then((res) => {
        setSubmitResult(res.data?.data);
        setSubmitState("done");
      })
      .catch((err) => {
        const existingUrl = err.response?.data?.verificationUrl;
        if (existingUrl) {
          setSubmitResult({ verificationUrl: existingUrl, alreadySent: true });
          setSubmitState("done");
        } else {
          setSubmitError(err.response?.data?.error || "Failed to send verification");
          setSubmitState("error");
        }
      });
  };

  if (submitState === "submitting") {
    return (
      <Fragment>
        <h1 style={{ textAlign: "center", fontSize: "28px" }}>
          Sending verification...
        </h1>
        <Loader />
      </Fragment>
    );
  }

  if (submitState === "done") {
    return (
      <SentView
        alreadySent={submitResult?.alreadySent}
        email={selectedMember?.email}
        verificationUrl={submitResult?.verificationUrl}
      />
    );
  }

  if (submitState === "error") {
    return (
      <Fragment>
        <h1 style={{ textAlign: "center", fontSize: "28px" }}>
          Something went wrong
        </h1>
        <p style={{ textAlign: "center" }}>{submitError}</p>
      </Fragment>
    );
  }

  if (eligibleMembers.length === 0) {
    return (
      <Fragment>
        <h1 style={{ textAlign: "center", fontSize: "28px" }}>
          {groupName ? `Send verification — ${groupName}` : "Send verification"}
        </h1>
        <p style={{ textAlign: "center" }}>
          All members of this group are either already in flight, missing an
          email, or are organizations. There's nothing to send right now.
        </p>
        <div style={{ marginTop: "20px" }}>
          <MemberRows members={members} selected={null} onSelect={() => {}} />
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <h1 style={{ textAlign: "center", fontSize: "28px" }}>
        {groupName ? `Send verification — ${groupName}` : "Send verification"}
      </h1>
      <p style={{ textAlign: "center" }}>
        Pick one member of this group to send a verification to. Members already
        in flight or otherwise unavailable are disabled.
      </p>

      <div style={{ marginTop: "20px" }}>
        <MemberRows
          members={members}
          selected={selected}
          onSelect={setSelected}
        />
      </div>

      <div
        className="flex justify-center items-center text-center"
        style={{ marginTop: "24px", gap: "12px" }}
      >
        <Button
          text="Send verification"
          action={handleSend}
          loading={false}
        />
      </div>
    </Fragment>
  );
}

function MemberRows({ members, selected, onSelect }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
      {members.map((m, idx) => {
        const key = m.contact_key || m.organization_key || `row-${idx}`;
        const disabled = !m.eligible;
        const reason = m.ineligibleReason
          ? INELIGIBLE_BADGE[m.ineligibleReason] || m.ineligibleReason
          : null;
        const name = [m.first_name, m.last_name].filter(Boolean).join(" ").trim();

        return (
          <li
            key={key}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "10px 12px",
              borderBottom: "1px solid #eee",
              opacity: disabled ? 0.55 : 1,
            }}
          >
            <input
              type="radio"
              name="group-member"
              value={m.contact_key || ""}
              disabled={disabled}
              checked={!disabled && selected === m.contact_key}
              onChange={() => m.contact_key && onSelect(m.contact_key)}
              style={{ marginRight: "12px" }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500 }}>{name || "Unnamed"}</div>
              {m.email && (
                <div style={{ fontSize: "0.9em", color: "#555" }}>{m.email}</div>
              )}
            </div>
            {reason && (
              <span
                style={{
                  fontSize: "0.8em",
                  padding: "2px 8px",
                  borderRadius: "10px",
                  background: "#f0f0f0",
                  color: "#555",
                  marginLeft: "8px",
                }}
              >
                {reason}
              </span>
            )}
            {m.existingVerificationUrl && (
              <a
                href={m.existingVerificationUrl}
                target="_blank"
                rel="noreferrer"
                style={{ marginLeft: "12px", fontSize: "0.85em" }}
              >
                View existing session
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function SentView({ alreadySent, email, verificationUrl }) {
  return (
    <Fragment>
      <h1 style={{ textAlign: "center", fontSize: "28px" }}>
        {alreadySent ? "Verification already sent" : "Verification sent"}
      </h1>
      <p style={{ textAlign: "center" }}>
        {alreadySent
          ? "The session is still active. The end client can complete it using the link below."
          : email
          ? `A verification email has been sent to ${email}.`
          : "A verification email has been sent."}
      </p>
      {verificationUrl && (
        <div
          className="flex justify-center items-center text-center"
          style={{ marginTop: "20px" }}
        >
          <Button
            text="View verification session"
            action={() => window.open(verificationUrl, "_blank")}
          />
        </div>
      )}
    </Fragment>
  );
}
